import isUndefined from 'lodash/isUndefined';
import {getRootStore} from '../../app.bootstrap';
import {IEventLogService} from '../../services/event-log/event-log.service';
import {TsSnapshotReminderService} from '../../services/ts-snapshot-reminder/TsSnapshotReminderService';
import {getDashboardStores} from '../../states/dashboard/dashboard.stores';
// @ts-ignore
import {compareUnderThreshold} from '@techsee/techsee-common/lib/utils';
import {action, observable} from 'mobx';

export type PopDirection = 'left' | 'center';
export type HideReason = 'snapshotTaken' | 'autoClose' | 'closedByAgent';

export interface ITsSnapshotReminderController {
    visible: boolean;
    popDirection: PopDirection;
    getContainerStyle(reminderRef: HTMLDivElement): {[key: string]: number} | undefined;
    closeClicked: () => void;
    dir: string;
}

export class TsSnapshotReminderController implements ITsSnapshotReminderController {
    @observable public visible: boolean;

    public popDirection: PopDirection;
    public dir: string;

    private previousLocation?: {top?: number; left?: number};
    private timer: ReturnType<typeof setTimeout> | null = null;
    private readonly eventLog: IEventLogService = getRootStore().eventLogService;
    private readonly snapshotReminderService: TsSnapshotReminderService = getDashboardStores().snapshotReminderService;
    private duration: number;
    private getReminderLocation: () => {top?: number; left?: number} | undefined;

    constructor(
        duration: number,
        getReminderLocation: () => {top?: number; left?: number} | undefined,
        popDirection: PopDirection,
        dir: string
    ) {
        this.duration = duration;
        this.getReminderLocation = getReminderLocation;
        this.popDirection = popDirection;
        this.dir = dir;
        this.visible = false;

        this.onSnapshotPopChanges = this.onSnapshotPopChanges.bind(this);
        this._snapshotTaken = this._snapshotTaken.bind(this);
        this.closeClicked = this.closeClicked.bind(this);

        this.init();
    }

    init(): void {
        this.snapshotReminderService.on('snapshotTaken', this._snapshotTaken);
        this.snapshotReminderService.on('snapshotPopChanges', this.onSnapshotPopChanges);
    }

    onSnapshotPopChanges(isPop: boolean) {
        if (isPop && !this.visible) {
            this.show();
        }
    }

    @action
    show(): void {
        if (this.timer) {
            clearTimeout(this.timer);
        }

        this.visible = true;

        // Keep same multiplier as legacy implementation
        this.timer = setTimeout(() => {
            this.hide({reason: 'autoClose'});
        }, this.duration * 1000);
    }

    _snapshotTaken() {
        this.hide({reason: 'snapshotTaken'});
    }

    @action
    hide(options: {reason: HideReason}): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }

        this.visible = false;

        this.eventLog.snapshotReminderClosed(options);
        this.snapshotReminderService.hide();
    }

    closeClicked() {
        this.hide({reason: 'closedByAgent'});
    }

    getContainerStyle(element: HTMLDivElement): {[key: string]: number} | undefined {
        if (!this.visible) {
            return;
        }

        const location = this.getReminderLocation();

        if (!location) {
            return {};
        }

        if (!isUndefined(location.top)) {
            const h = element.clientHeight;
            location.top = Math.ceil(location.top - h - 22);
        }

        if (!isUndefined(location.left)) {
            if (this.popDirection === 'left') {
                location.left = Math.ceil(location.left - 23);
            } else {
                const w = element.clientWidth;
                location.left = Math.ceil(location.left - w / 2 + 21);
            }
        }

        if (!(location.top === 0 && location.left === 0)) {
            if (
                this.previousLocation &&
                !compareUnderThreshold(this.previousLocation.left, location.left) &&
                !compareUnderThreshold(this.previousLocation.top, location.top)
            ) {
                return this.previousLocation as any;
            }
            this.previousLocation = location;
        }

        return location as any;
    }
}
